/**
 * VitalsSummaryWidget — At-a-glance vitals display (DRR-319)
 *
 * @deprecated Use MetricsSummaryWidget from @ferp/workflow-forms/widgets instead.
 * This widget has hardcoded vital types and thresholds which violates DRY.
 * The generic MetricsSummaryWidget gets metrics/thresholds from backend config.
 *
 * This file is kept for backward compatibility but should not be used
 * in new dashboard configurations.
 *
 * FHIR data: Observations with LOINC codes for vital signs.
 */

import React from 'react';
import { Group, Text, Badge, Stack, Paper, Skeleton, Tooltip } from '@mantine/core';
import { registerDashboardWidget, type DashboardWidgetProps } from '@ferp/workflow-forms/widgets';
import {
  IconHeartRateMonitor,
  IconScale,
  IconDroplet,
  IconLungs,
  IconArrowUp,
  IconArrowDown,
  IconMinus,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const GET_LATEST_VITALS = gql`
  query GetLatestVitals($tenantNum: Int!, $subjectActorId: String!) {
    fhirLatestVitals(tenantNum: $tenantNum, subjectActorId: $subjectActorId) {
      bloodPressure {
        systolic
        diastolic
        timestamp
        trend
      }
      heartRate {
        value
        timestamp
        trend
      }
      weight {
        value
        unit
        timestamp
        changeFromPrevious
      }
      oxygenSaturation {
        value
        timestamp
      }
      temperature {
        value
        unit
        timestamp
      }
    }
  }
`;

export interface VitalsSummaryWidgetProps {
  tenantNum: number;
  subjectActorId: string;
  compact?: boolean;
}

interface TrendIndicatorProps {
  trend?: 'up' | 'down' | 'stable' | null;
  value?: number | null;
}

function TrendIndicator({ trend, value }: TrendIndicatorProps) {
  if (!trend && !value) return null;

  if (value !== null && value !== undefined) {
    const color = value > 0 ? 'red' : value < 0 ? 'green' : 'gray';
    const Icon = value > 0 ? IconArrowUp : value < 0 ? IconArrowDown : IconMinus;
    return (
      <Tooltip label={`${value > 0 ? '+' : ''}${value.toFixed(1)} change`}>
        <Badge size="xs" color={color} variant="light" p={2}>
          <Icon size={10} />
        </Badge>
      </Tooltip>
    );
  }

  if (trend === 'up') return <IconArrowUp size={12} color="orange" />;
  if (trend === 'down') return <IconArrowDown size={12} color="blue" />;
  return <IconMinus size={12} color="gray" />;
}

export function VitalsSummaryWidget({ tenantNum, subjectActorId, compact = false }: VitalsSummaryWidgetProps) {
  const { data, loading } = useQuery(GET_LATEST_VITALS, {
    variables: { tenantNum, subjectActorId },
    skip: !tenantNum || !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const vitals = data?.fhirLatestVitals;

  if (loading && !vitals) {
    return (
      <Group gap="md">
        <Skeleton height={40} width={80} />
        <Skeleton height={40} width={60} />
        <Skeleton height={40} width={60} />
        <Skeleton height={40} width={60} />
      </Group>
    );
  }

  if (!vitals) {
    return (
      <Text size="xs" c="dimmed">No vitals recorded</Text>
    );
  }

  const items = [
    vitals.bloodPressure && {
      label: 'BP',
      value: `${vitals.bloodPressure.systolic}/${vitals.bloodPressure.diastolic}`,
      icon: IconDroplet,
      color: vitals.bloodPressure.systolic > 140 ? 'red' : 'blue',
      trend: vitals.bloodPressure.trend,
    },
    vitals.heartRate && {
      label: 'HR',
      value: `${vitals.heartRate.value}`,
      icon: IconHeartRateMonitor,
      color: vitals.heartRate.value > 100 ? 'orange' : 'green',
      trend: vitals.heartRate.trend,
    },
    vitals.weight && {
      label: 'Wt',
      value: `${vitals.weight.value}`,
      unit: vitals.weight.unit || 'lbs',
      icon: IconScale,
      color: 'gray',
      change: vitals.weight.changeFromPrevious,
    },
    vitals.oxygenSaturation && {
      label: 'SpO2',
      value: `${vitals.oxygenSaturation.value}%`,
      icon: IconLungs,
      color: vitals.oxygenSaturation.value < 95 ? 'red' : 'green',
    },
  ].filter(Boolean);

  if (compact) {
    return (
      <Group gap="xs">
        {items.map((item: any, i) => (
          <Badge key={i} size="sm" color={item.color} variant="light">
            {item.label}: {item.value}{item.unit ? ` ${item.unit}` : ''}
          </Badge>
        ))}
      </Group>
    );
  }

  return (
    <Group gap="md">
      {items.map((item: any, i) => (
        <Paper key={i} p="xs" withBorder radius="sm">
          <Stack gap={2} align="center">
            <Group gap={4}>
              <item.icon size={14} color={item.color === 'gray' ? undefined : item.color} />
              <Text size="xs" c="dimmed">{item.label}</Text>
            </Group>
            <Group gap={4}>
              <Text fw={600} size="sm">{item.value}</Text>
              {item.unit && <Text size="xs" c="dimmed">{item.unit}</Text>}
              <TrendIndicator trend={item.trend} value={item.change} />
            </Group>
          </Stack>
        </Paper>
      ))}
    </Group>
  );
}

const VitalsSummaryDashboardWidget: React.FC<DashboardWidgetProps> = ({
  tenantNum,
  subjectActorId,
  config
}) => {
  const compact = config?.compact as boolean ?? false;
  return <VitalsSummaryWidget tenantNum={tenantNum} subjectActorId={subjectActorId} compact={compact} />;
};

registerDashboardWidget('vitals-summary', VitalsSummaryDashboardWidget, 'fhir', 'Latest vital signs with trends');
